import { Component, EventEmitter, Input, OnDestroy, Output, Renderer2 } from "@angular/core";
import { Subscription } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { HeicConverterService } from "src/app/shared/services/heic-converter.service";
import { MultipartS3UploadService } from "src/app/shared/services/multipart-s3-upload.service";
import { UploadObsService } from "src/app/shared/services/upload-obs.service";
import { VideoThumbnailService } from "src/app/shared/services/video-thumbnail.service";

@Component({
  selector: "app-tab-brand-materials",
  templateUrl: "./tab-brand-materials.component.html",
  styleUrls: ["./tab-brand-materials.component.scss"],
})
export class TabBrandMaterialsComponent implements OnDestroy {
  @Input() model: any = {};
  @Input() formValid: boolean = true;
  @Input() submitted: boolean = false;
  @Input() fonts: string[] = [];
  @Input() uploadProgress: { [key: string]: number } = {};
  @Input() uploadStates: { [key: string]: boolean } = {};

  @Output() modelChange = new EventEmitter<any>();
  @Output() autoCheckEvent = new EventEmitter<string>();

  brandLogoFile: File;
  inspirationalVideoFile: File;

  // Track active upload subscriptions for cancellation
  private activeUploads: Map<string, { subscription: Subscription }> = new Map();

  constructor(
    private toastr: ToastrService,
    private uploadService: UploadObsService,
    private multipartUploadService: MultipartS3UploadService,
    private renderer: Renderer2,
    private uploadThumbnailService: VideoThumbnailService,
    private heicConverter: HeicConverterService
  ) {}

  autoCheck(field: string) {
    this.autoCheckEvent.emit(field);
  }

  onFontChange(font: string, fontSelectBox: HTMLElement) {
    this.model.brandFont = font;
    this.renderer.setStyle(fontSelectBox, "font-family", font + "!important");
    this.modelChange.emit(this.model);
  }

  async uploadBrandLogoFile(e: File[]) {
    if (!this.isFileSizeValid(e[0])) {
      this.toastr.error("Max file size exceeds 50MB", "Error");
      return;
    }
    this.brandLogoFile = e[0];
    if (this.isFileTypeValid(this.brandLogoFile)) {
      this.brandLogoFile = await this.heicConverter.convertToJpegIfNeeded(this.brandLogoFile);
      const uploadKey = this.generateUploadKey("brand_logo", this.brandLogoFile.name);
      this.setUploadState(uploadKey, true);
      this.model.brandLogoUploadKey = uploadKey;
      this.model.brandLogo = null;
      const brandLogoSubscription = this.uploadService.uploadFileToS3("digital/photos", this.brandLogoFile).subscribe(
        (response: any) => {
          console.log(response);
          if (response && response.type === "progress") {
            this.setUploadProgress(uploadKey, response.percent);
            return;
          }

          if (response && response.type === "complete") {
            this.setUploadState(uploadKey, false);
            this.model.brandLogo = response.url;
            this.toastr.success(`Brand Logo ${this.brandLogoFile.name} uploaded successfully`);
            this.model.brandLogoUploadKey = null;
            this.cleanupUpload(uploadKey);
            this.activeUploads.delete(uploadKey);
            this.modelChange.emit(this.model);
          }
        },
        error => {
          this.setUploadState(uploadKey, false);
          this.toastr.error(`Failed to upload ${this.brandLogoFile.name}: ${error.message || error}`);
          console.error("Upload error:", error);
          this.model.brandLogoUploadKey = null;
          this.cleanupUpload(uploadKey);
          this.activeUploads.delete(uploadKey);
        }
      );
      this.activeUploads.set(uploadKey, { subscription: brandLogoSubscription });
    } else {
      this.toastr.error("Invalid file type");
    }
  }

  removeBrandLogo() {
    this.model.brandLogo = null;
    this.modelChange.emit(this.model);
  }

  uploadInspirationalVideoFile(f: File[]) {
    this.inspirationalVideoFile = f[0];
    if (this.inspirationalVideoFile && this.inspirationalVideoFile.type.startsWith("video/")) {
      const uploadKey = this.generateUploadKey("inspirational_video", this.inspirationalVideoFile.name);
      this.setUploadState(uploadKey, true);
      this.model.inspirationalVideoUploadKey = uploadKey;
      // Không set inspirationalVideo = null ở đây để tránh mất dữ liệu khi đang upload
      // Chỉ set khi upload thực sự bắt đầu và có thể theo dõi qua uploadKey

      // Track thumbnail upload subscription
      const thumbnailKey = `${uploadKey}_thumbnail`;
      const thumbnailSubscription = this.uploadThumbnailService
        .generateThumbnailFromVideo(this.inspirationalVideoFile)
        .subscribe(
          (response: any) => {
            if (response && response.type === "complete") {
              this.model.inspirationalVideoThumbnail = response.url;
              // Emit chỉ thumbnail, không emit inspirationalVideo để tránh ghi đè
              // Chỉ emit field thumbnail, không emit inspirationalVideo nếu chưa có giá trị hợp lệ
              const updateData: any = {
                inspirationalVideoThumbnail: response.url,
              };
              // Chỉ thêm inspirationalVideo nếu đã có giá trị hợp lệ
              if (
                this.model.inspirationalVideo &&
                typeof this.model.inspirationalVideo === "string" &&
                this.model.inspirationalVideo.trim() !== ""
              ) {
                updateData.inspirationalVideo = this.model.inspirationalVideo;
              }
              this.modelChange.emit(updateData);
              this.activeUploads.delete(thumbnailKey);
            }
          },
          error => {
            console.error("Thumbnail generation failed:", error);
            this.toastr.warning("Thumbnail generation failed, but video upload will continue");
            this.activeUploads.delete(thumbnailKey);
          }
        );
      this.activeUploads.set(thumbnailKey, { subscription: thumbnailSubscription });

      // Track video upload (multipart — same S3 flow as section videos)
      const videoSubscription = this.multipartUploadService
        .uploadVideoMultipart("digital/videos", this.inspirationalVideoFile, {
          maxSizeMB: 5120,
          chunkSizeMB: 15,
          fileId: uploadKey,
        })
        .subscribe(
          (response: any) => {
            console.log(response);
            if (response && response.type === "progress") {
              this.setUploadProgress(uploadKey, response.percent);
              return;
            }

            if (response && response.type === "complete") {
              this.setUploadState(uploadKey, false);
              // Đảm bảo cập nhật đúng giá trị và emit với toàn bộ model
              const updatedModel = {
                ...this.model,
                inspirationalVideo: response.url,
                inspirationalVideoUploadKey: null,
              };
              this.model.inspirationalVideo = updatedModel.inspirationalVideo;
              this.model.inspirationalVideoUploadKey = null;
              this.autoCheck("displayInspirationalVideo");
              this.cleanupUpload(uploadKey);
              this.activeUploads.delete(uploadKey);
              // Emit với toàn bộ model để đảm bảo sync đúng
              this.modelChange.emit(updatedModel);
            }
          },
          error => {
            // Ignore error if upload was cancelled
            if (error.message && error.message.includes("cancelled")) {
              this.activeUploads.delete(uploadKey);
              return;
            }
            this.setUploadState(uploadKey, false);
            this.model.inspirationalVideoUploadKey = null;
            this.toastr.error(`Failed to upload: ${error.message || error}`);
            console.error("Upload error:", error);
            this.cleanupUpload(uploadKey);
            this.activeUploads.delete(uploadKey);
            // Emit để cập nhật trạng thái lỗi
            this.modelChange.emit({ ...this.model });
          }
        );
      this.activeUploads.set(uploadKey, { subscription: videoSubscription });
    }
  }

  removeInspirationalVideo() {
    // Cancel uploads if in progress
    const uploadKey = this.model.inspirationalVideoUploadKey;
    if (uploadKey) {
      // Cancel video upload
      const videoUpload = this.activeUploads.get(uploadKey);
      if (videoUpload && videoUpload.subscription && !videoUpload.subscription.closed) {
        videoUpload.subscription.unsubscribe();
        this.activeUploads.delete(uploadKey);
      }

      // Cancel thumbnail upload
      const thumbnailKey = `${uploadKey}_thumbnail`;
      const thumbnailUpload = this.activeUploads.get(thumbnailKey);
      if (thumbnailUpload && thumbnailUpload.subscription && !thumbnailUpload.subscription.closed) {
        thumbnailUpload.subscription.unsubscribe();
        this.activeUploads.delete(thumbnailKey);
      }

      // Cleanup upload state and progress
      this.cleanupUpload(uploadKey);
      this.model.inspirationalVideoUploadKey = null;
    }

    // Clear video data
    this.model.inspirationalVideo = null;
    this.model.inspirationalVideoThumbnail = null;
    this.modelChange.emit(this.model);
  }

  async uploadBannerFile(files: File[]) {
    if (!files || files.length === 0) {
      return;
    }
    let file = files[0];

    // Validate file type
    if (!this.isFileTypeValid(file)) {
      this.toastr.error("Invalid file type", "Error");
      return;
    }

    // Validate file size
    if (!this.isFileSizeValid(file)) {
      this.toastr.error("Max file size exceeds 50MB", "Error");
      return;
    }

    file = await this.heicConverter.convertToJpegIfNeeded(file);

    const uploadKey = this.generateUploadKey("banner", file.name);
    this.setUploadState(uploadKey, true);
    this.model.bannerUploadKey = uploadKey;
    const bannerSubscription = this.uploadService.uploadFileToS3("digital/photos", file).subscribe(
      (response: any) => {
        console.log(response);
        if (response && response.type === "progress") {
          this.setUploadProgress(uploadKey, response.percent);
          return;
        }

        if (response && response.type === "complete") {
          this.setUploadState(uploadKey, false);
          this.model.banner = response.url;
          this.toastr.success("Banner uploaded successfully");
          this.model.bannerUploadKey = null;
          this.cleanupUpload(uploadKey);
          this.activeUploads.delete(uploadKey);
          this.modelChange.emit(this.model);
        }
      },
      error => {
        this.setUploadState(uploadKey, false);
        this.toastr.error(`Failed to upload ${file.name}: ${error.message || error}`);
        console.error("Upload error:", error);
        this.model.bannerUploadKey = null;
        this.cleanupUpload(uploadKey);
        this.activeUploads.delete(uploadKey);
      }
    );
    this.activeUploads.set(uploadKey, { subscription: bannerSubscription });
  }

  removeBanner(type?: string) {
    this.model.banner = null;
    // Clear the file input
    const fileInput = document.getElementById("changeBanner") as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
    this.modelChange.emit(this.model);
  }

  // Helper methods
  private isFileSizeValid(file: File): boolean {
    const maxSize = 50 * 1024 * 1024; // 50MB
    return file.size <= maxSize;
  }

  private isFileTypeValid(file: File): boolean {
    const validImageTypes = ["image/gif", "image/jpeg", "image/png", "image/heic", "image/heif"];
    const isHeicByExtension = /\.(heic|heif)$/i.test(file.name);
    return (
      validImageTypes.includes(file.type) ||
      (isHeicByExtension && (!file.type || file.type === "application/octet-stream"))
    );
  }

  private generateUploadKey(prefix: string, fileName: string): string {
    return `${prefix}_${Date.now()}_${fileName}`;
  }

  private setUploadState(key: string, isUploading: boolean) {
    this.uploadStates[key] = isUploading;
  }

  private setUploadProgress(key: string, percent: number) {
    this.uploadProgress[key] = percent;
  }

  isUploadInProgress(key: string): boolean {
    return this.uploadStates[key] || false;
  }

  getProgressByKey(key: string): number {
    return this.uploadProgress[key] || 0;
  }

  private isInspirationalVideoUploading(): boolean {
    if (!this.model || !this.model.inspirationalVideoUploadKey) {
      return false;
    }

    const uploadKey = this.model.inspirationalVideoUploadKey;
    const isUploading = this.uploadStates[uploadKey] === true;
    const progress = this.uploadProgress[uploadKey];
    const isProgressing = progress !== undefined && progress > 0 && progress < 100;

    return isUploading || isProgressing;
  }

  isInspirationalVideoRequiredError(): boolean {
    return (
      !!this.model.displayInspirationalVideo && !this.model.inspirationalVideo && !this.isInspirationalVideoUploading()
    );
  }

  private cleanupUpload(key: string) {
    delete this.uploadProgress[key];
    delete this.uploadStates[key];
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  ngOnDestroy(): void {
    this.activeUploads.forEach((upload, uploadKey) => {
      if (upload && upload.subscription && !upload.subscription.closed) {
        upload.subscription.unsubscribe();
      }
      this.cleanupUpload(uploadKey);
    });
    this.activeUploads.clear();
  }
}
